import { createContext, useContext } from 'react'
import { createStore } from 'zustand/vanilla'
import { useStore } from 'zustand'
import { FrontendOptionState, IGetTradesResponse, IOrderModifyData, IUnderlyingOrderModifyData, LiveMarketData } from './interfaces';



// UNDERLYING ORDER MODIFY OR CREATE DATA STORE
type UnderlyingOrderModifyDataType = { 
  underlyingOrderModifyData: IUnderlyingOrderModifyData; 
  setNewOrderModifyData: (conId: string, orderId: string, action: string, readable_name: string, price: string, total: string, size: string) => void;
  setPrice: (price: string) => void;
  setSize: (size: string) => void;
  setAction: (action: string) => void;
  reset: () => void;
}

const createUnderlyingOrderModifyDataStore = () => createStore<UnderlyingOrderModifyDataType>((set) => ({
  underlyingOrderModifyData: {
    conId: "",
    orderId: "",
    action: "",
    readable_name: "",
    price: "",
    total: "",
    size: "",
  },

  reset: () => set(() => ({
    underlyingOrderModifyData: {
      conId: "",
      orderId: "",
      action: "",
      readable_name: "",
      price: "",
      total: "",
      size: "",
    },
  })),

  setNewOrderModifyData: (
    conId: string,
    orderId: string,
    action: string,
    readable_name: string,
    price: string,
    total: string,
    size: string,
  ) =>
    set(() => ({
      underlyingOrderModifyData: {
        conId,
        orderId,
        action,
        readable_name,
        price,
        total,
        size,
      },
    })),

  setPrice: (price: string) =>
    set((state) => ({
      underlyingOrderModifyData: { ...state.underlyingOrderModifyData, price, total: (Number(price) * Number(state.underlyingOrderModifyData.size || 0)).toFixed(2) },
    })),

  setSize: (size: string) => set((state) => ({
    underlyingOrderModifyData: {
      ...state.underlyingOrderModifyData,
      size,
      total: (Number(state.underlyingOrderModifyData.price) * Number(size || 0)).toFixed(2),
    },
  })),

  setAction: (action: string) => set((state) => ({
    underlyingOrderModifyData: { ...state.underlyingOrderModifyData, action },
  })),
}));

// ORDER MODIFY OR CREATE DATA STORE
type OrderModifyDataType = {
  orderModifyData: IOrderModifyData;
  setNewOrderModifyData: (conId: string, orderId: string, action: string, readable_name: string, price: string, total: string, size: string) => void;
  setIv: (iv: string) => void;
  setPrice: (price: string) => void;
  setSize: (size: string) => void;
  setAction: (action: string) => void;
  reset: () => void;
};

const createOrderModifyDataStore = () => createStore<OrderModifyDataType>((set) => ({
  orderModifyData: {
    conId: "",
    orderId: "",
    action: "",
    readable_name: "",
    price: "",
    total: "",
    size: "",
    iv: "",
  },

  reset: () => set(() => ({
    orderModifyData: {
      conId: "",
      orderId: "",
      action: "",
      readable_name: "",
      price: "",
      total: "",
      size: "",
      iv: "",
    },
  })),

  setNewOrderModifyData: (
    conId: string,
    orderId: string,
    action: string,
    readable_name: string,
    price: string,
    total: string,
    size: string,
  ) =>
    set(() => ({
      orderModifyData: {
        conId,
        orderId,
        action,
        readable_name,
        price,
        total,
        size,
        iv: "",
      },
    })),

  setIv: (iv: string) =>
    set((state) => ({
      orderModifyData: { ...state.orderModifyData, iv },
    })),

  setPrice: (price: string) =>
    set((state) => {
      const priceNum = Number(price);
      const sizeNum = Number(state.orderModifyData.size);
      const isValid = !isNaN(priceNum) && !isNaN(sizeNum);
      return {
        orderModifyData: {
          ...state.orderModifyData,
          price,
          total: isValid ? (priceNum * sizeNum * 100).toFixed(2) : "N/A",
        },
      };
    }),

  setSize: (size: string) => set((state) => ({
    orderModifyData: {
      ...state.orderModifyData,
      size,
      total: (Number(state.orderModifyData.price) * Number(size || 0) * 100).toFixed(2),
    },
  })),

  setAction: (action: string) => set((state) => ({
    orderModifyData: { ...state.orderModifyData, action },
  })),
}));


// LIVE MARKET DATA STORE
type LiveMarketDataType = {
  liveMarketData: LiveMarketData[];
  updateLiveMarketData: (updates: LiveMarketData[]) => void;
  resetLiveMarketData: () => void;
};

const createLiveMarketDataStore = () => createStore<LiveMarketDataType>((set) => ({
  liveMarketData: [],

  updateLiveMarketData: (updates: LiveMarketData[]) => {
    set((state) => {
      let newData = [...state.liveMarketData];
      for (const update of updates) {
        const index = newData.findIndex((d) => d.conId === update.conId);
        if (index !== -1) {
          newData[index] = { ...newData[index], ...update };
        } else {
          newData.push(update);
        }
      }
      return { liveMarketData: newData };
    });
  },

  resetLiveMarketData: () => set({ liveMarketData: [] }),
}));


// TRADES STORE
type TradesState = {
  trades: IGetTradesResponse[];
  setTrades: (trades: IGetTradesResponse[]) => void;
};

const createTradesStore = () => createStore<TradesState>((set) => ({
  trades: [],
  setTrades: (trades: IGetTradesResponse[]) => set(() => ({ trades })),
}));


// FRONTEND OPTION STATE STORE
type FrontendOptionStateType = {
  frontendOptionState: FrontendOptionState[];
  setFrontendOptionState: (options: FrontendOptionState[]) => void;
  resetFrontendOptionState: () => void;
};

const createFrontendOptionStateStore = () => createStore<FrontendOptionStateType>((set) => ({
  frontendOptionState: [],
  setFrontendOptionState: (options: FrontendOptionState[]) => set(() => ({ frontendOptionState: options })),
  resetFrontendOptionState: () => set(() => ({ frontendOptionState: [] })),
}));


// POSITIONS STORE
interface Position {
  conId: number;
  readable_name: string;
  size: number;
  avgCost: number;
}

type PositionsState = {
  positions: Position[];
  setPositions: (positions: Position[]) => void;
  resetPositions: () => void;
};

const createPositionsStore = () => createStore<PositionsState>((set) => ({
  positions: [],
  setPositions: (positions: Position[]) => set(() => ({ positions })),
  resetPositions: () => set(() => ({ positions: [] })),
}));


// ACCOUNTS STORE — populated from the "accounts" message the backend sends
// right after connecting, when the login has visibility into more than one
// IBKR account (e.g. a sub-account structure).
type AccountsState = {
  accounts: string[];
  selectedAccount: string | null;
  setAccounts: (accounts: string[], selected: string | null) => void;
  setSelectedAccount: (account: string) => void;
};

const createAccountsStore = () => createStore<AccountsState>((set) => ({
  accounts: [],
  selectedAccount: null,
  setAccounts: (accounts: string[], selected: string | null) => set(() => ({ accounts, selectedAccount: selected })),
  setSelectedAccount: (account: string) => set(() => ({ selectedAccount: account })),
}));


// CONTEXT — one set of stores per IBKR panel instance
export type IBKRStores = {
  orderModifyData: ReturnType<typeof createOrderModifyDataStore>;
  underlyingOrderModifyData: ReturnType<typeof createUnderlyingOrderModifyDataStore>;
  liveMarketData: ReturnType<typeof createLiveMarketDataStore>;
  trades: ReturnType<typeof createTradesStore>;
  frontendOptionState: ReturnType<typeof createFrontendOptionStateStore>;
  positions: ReturnType<typeof createPositionsStore>;
  accounts: ReturnType<typeof createAccountsStore>;
};

export const IBKRStoreContext = createContext<IBKRStores | null>(null);

export function createIBKRStores(): IBKRStores {
  return {
    orderModifyData: createOrderModifyDataStore(),
    underlyingOrderModifyData: createUnderlyingOrderModifyDataStore(),
    liveMarketData: createLiveMarketDataStore(),
    trades: createTradesStore(),
    frontendOptionState: createFrontendOptionStateStore(),
    positions: createPositionsStore(),
    accounts: createAccountsStore(),
  };
}

function useIBKRStores(): IBKRStores {
  const ctx = useContext(IBKRStoreContext);
  if (!ctx) throw new Error('useIBKRStores must be used inside IBKRStoreProvider');
  return ctx;
}


// Drop-in replacement hooks — same API, now per-instance
export const useOrderModifyDataStore = () => useStore(useIBKRStores().orderModifyData);
export const useUnderlyingOrderModifyDataStore = () => useStore(useIBKRStores().underlyingOrderModifyData);
export const useLiveMarketDataStore = () => useStore(useIBKRStores().liveMarketData);
export const useTradesStore = () => useStore(useIBKRStores().trades);
export const useFrontendOptionState = () => useStore(useIBKRStores().frontendOptionState);
export const usePositionsStore = () => useStore(useIBKRStores().positions);
export const useAccountsStore = () => useStore(useIBKRStores().accounts);